import React, { useEffect } from "react";
import {
  ArrowLeft,
  CalendarDays,
  User,
  ChevronLeft,
  Trash2,
  MoreVertical,
  Pencil,
  IndianRupee,
  Download,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_URL } from "utils/constant";
import { useAlert } from "components/UI_components/Alert";
import axios from "axios";
import Icon from "@mdi/react";
import { mdiAccountHardHat } from "@mdi/js";
import ConfirmationModal from "components/ConfirmationModal";
import { pdf } from "@react-pdf/renderer";
import WorkerPaymentReceiptPDFDocument from "./WorkerPaymentReceiptPDFDocument";

const PerformanceRing = ({ percent = 88 }) => {
  const angle = Math.round((percent / 100) * 360);
  return (
    <div className="relative w-28 h-28">
      <div
        className="w-28 h-28 rounded-full"
        style={{
          background: `conic-gradient(#6A32A8 0deg ${angle}deg, #EFE8F6 ${angle}deg 360deg)`,
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center shadow-inner">
          <span className="text-xl font-semibold text-[#3B0069]">
            {percent}%
          </span>
        </div>
      </div>
    </div>
  );
};

const MiniBar = ({ height = 60, active = false }) => (
  <div
    className={`${active ? "bg-[#6A32A8]" : "bg-[#EDE7F4]"} rounded-lg w-full`}
    style={{ height }}
  />
);

const PerformancePattern = ({ values = [2, 3, 5, 4] }) => {
  const weekLabels = ["Week 1", "Week 2", "Week 3", "Week 4"];
  // values: weekly present counts for current month (length 4)
  const maxVal = Math.max(1, ...values);
  // Normalize to [10, 90] for a pleasant curve
  const points = values.map((v, i) => ({
    x: (i / (values.length - 1)) * 600,
    y: 10 + (v / maxVal) * 80,
  }));

  // Build cubic bezier path through points
  const path = points.reduce((acc, p, i, arr) => {
    if (i === 0) return `M${p.x} ${p.y}`;
    const prev = arr[i - 1];
    const cx1 = prev.x + (p.x - prev.x) / 3;
    const cy1 = prev.y;
    const cx2 = prev.x + (2 * (p.x - prev.x)) / 3;
    const cy2 = p.y;
    return acc + ` C ${cx1} ${cy1}, ${cx2} ${cy2}, ${p.x} ${p.y}`;
  }, "");

  return (
    <svg viewBox="0 0 600 150" className="w-full h-32 text-[#6A32A8]">
      <g transform="translate(0,150) scale(1,-1)">
        <path d={path} fill="none" stroke="currentColor" strokeWidth="3" />
        {points.map((point, i) => (
          <circle
            key={`week-point-${i}`}
            cx={point.x}
            cy={point.y}
            r={14}
            fill="rgba(0,0,0,0.001)"
          >
            <title>{`${weekLabels[i] || `Week ${i + 1}`}: ${Number(
              values[i] || 0,
            ).toLocaleString("en-IN")}`}</title>
          </circle>
        ))}
      </g>
    </svg>
  );
};

// Dynamic donut pie for Attendance distribution using conic-gradient
const AttendancePie = ({ fullCount = 0, halfCount = 0, overtimeCount = 0 }) => {
  const total =
    Math.max(0, fullCount) +
    Math.max(0, halfCount) +
    Math.max(0, overtimeCount);
  const fullPct = total > 0 ? fullCount / total : 0;
  const halfPct = total > 0 ? halfCount / total : 0;
  const overtimePct = total > 0 ? 1 - fullPct - halfPct : 0; // remainder

  const fullDeg = Math.round(fullPct * 360);
  const halfDeg = Math.round(halfPct * 360);
  const overtimeDeg = Math.max(0, 360 - fullDeg - halfDeg);

  const background =
    total === 0
      ? `conic-gradient(#EDE7F4 0deg 360deg)`
      : `conic-gradient(#6A32A8 0deg ${fullDeg}deg, #BBA6E9 ${fullDeg}deg ${
          fullDeg + halfDeg
        }deg, #C9B7F1 ${fullDeg + halfDeg}deg ${
          fullDeg + halfDeg + overtimeDeg
        }deg)`;

  return (
    <div className="group relative w-40 h-40 cursor-pointer">
      <div className="absolute bottom-full left-1/2 z-20 mb-2 hidden -translate-x-1/2 group-hover:block">
        <div className="relative whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white">
          <div>{`Full Day: ${Number(fullCount || 0).toLocaleString("en-IN")}`}</div>
          <div>{`Half Day: ${Number(halfCount || 0).toLocaleString("en-IN")}`}</div>
          <div>{`Overtime Day: ${Number(overtimeCount || 0).toLocaleString("en-IN")}`}</div>
          <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      </div>
      <div
        className="w-40 h-40 rounded-full shadow-inner"
        style={{ background }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-24 h-24 bg-white rounded-full" />
      </div>
    </div>
  );
};

const MonthBar = ({ height = 80, active = false }) => (
  <div
    className={`${
      active ? "bg-[#6A32A8]" : "bg-[#EDE7F4]"
    } rounded-xl w-14 md:w-16`}
    style={{ height }}
  />
);

// Finance visuals
const FinanceDonut = ({
  basic = 0.5,
  overtime = 0.3,
  advance = 0.0,
  deduction = 0.2,
  values = {},
}) => {
  const basicDeg = Math.round(basic * 360);
  const overtimeDeg = Math.round(overtime * 360);
  const advanceDeg = Math.round(advance * 360);
  const deductionDeg = 360 - basicDeg - overtimeDeg - advanceDeg;
  const basicAmount = Number(values.basic || 0);
  const overtimeAmount = Number(values.overtime || 0);
  const deductionAmount = Number(values.deduction || 0);
  const tooltipText = `Basic: Rs. ${basicAmount.toLocaleString(
    "en-IN",
  )}\nOvertime: Rs. ${overtimeAmount.toLocaleString(
    "en-IN",
  )}\nDeduction: Rs. ${deductionAmount.toLocaleString("en-IN")}`;
  return (
    <div className="group relative w-32 h-32 cursor-pointer">
      <div className="absolute bottom-full left-1/2 z-20 mb-2 hidden -translate-x-1/2 group-hover:block">
        <div className="relative whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white">
          <div>{`Basic: Rs. ${basicAmount.toLocaleString("en-IN")}`}</div>
          <div>{`Overtime: Rs. ${overtimeAmount.toLocaleString("en-IN")}`}</div>
          <div>{`Deduction: Rs. ${deductionAmount.toLocaleString("en-IN")}`}</div>
          <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      </div>
      <div
        className="w-32 h-32 rounded-full shadow-inner"
        style={{
          background: `conic-gradient(#C9B7F1 0deg ${basicDeg}deg, #6A32A8 ${basicDeg}deg ${
            basicDeg + overtimeDeg
          }deg, #8F6AD6 ${basicDeg + overtimeDeg}deg ${
            basicDeg + overtimeDeg + advanceDeg
          }deg, #EDE7F4 ${basicDeg + overtimeDeg + advanceDeg}deg ${
            basicDeg + overtimeDeg + advanceDeg + deductionDeg
          }deg)`,
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-18 h-18 bg-white rounded-full" />
      </div>
      <svg viewBox="0 0 128 128" className="absolute inset-0">
        <circle cx="64" cy="64" r="62" fill="rgba(0,0,0,0.001)">
          <title>{tooltipText}</title>
        </circle>
      </svg>
    </div>
  );
};

const AmountBar = ({ height = 80, active = false, label = "", value = 0 }) => (
  <div className="group relative flex w-12 items-end justify-center">
    <div
      className={`${
        active ? "bg-[#6A32A8]" : "bg-[#EDE7F4]"
      } w-12 rounded-[10px]`}
      style={{ height }}
    />
    <div className="absolute bottom-full left-1/2 z-20 mb-2 hidden -translate-x-1/2 group-hover:block">
      <div className="relative whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white">
        {`${label}: Rs. ${Number(value || 0).toLocaleString("en-IN")}`}
        <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
      </div>
    </div>
    <svg
      viewBox={`0 0 48 ${Math.max(1, Number(height || 1))}`}
      className="absolute inset-0 h-full w-full"
      preserveAspectRatio="none"
    >
      <rect
        x="0"
        y="0"
        width="48"
        height={Math.max(1, Number(height || 1))}
        rx="10"
        ry="10"
        fill="rgba(0,0,0,0.001)"
      >
        <title>{`${label}: Rs. ${Number(value || 0).toLocaleString("en-IN")}`}</title>
      </rect>
    </svg>
  </div>
);

const FinanceTrend = ({ values = [] }) => {
  const monthLabels = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const normalizedValues = monthLabels.map((_, i) => Number(values?.[i] || 0));
  const n = normalizedValues.length || 1;
  const maxVal = Math.max(1, ...normalizedValues);

  // Fixed width for 12 months alignment
  const padding = 40; // padding on each side
  const width = 880; // adjusted for 12 months (73.33 per month)
  const chartWidth = width - 2 * padding;
  const step = chartWidth / (n - 1 || 1);

  const points = normalizedValues.map((v, i) => ({
    x: padding + i * step,
    y: 10 + (v / maxVal) * 140,
  }));

  const d = points.reduce((acc, p, i, arr) => {
    if (i === 0) return `M${p.x} ${p.y}`;
    const prev = arr[i - 1];
    const cx1 = prev.x + (p.x - prev.x) / 3;
    const cy1 = prev.y;
    const cx2 = prev.x + (2 * (p.x - prev.x)) / 3;
    const cy2 = p.y;
    return acc + ` C ${cx1} ${cy1}, ${cx2} ${cy2}, ${p.x} ${p.y}`;
  }, "");

  return (
    <div className="relative h-40 w-full">
      <svg viewBox={`0 0 ${width} 160`} className="h-40 w-full text-[#6A32A8]">
        <g transform="translate(0,160) scale(1,-1)">
          <path d={d} fill="none" stroke="currentColor" strokeWidth="3" />
        </g>
      </svg>

      <div className="absolute inset-0">
        {points.map((point, i) => {
          const valueLabel = `${monthLabels[i]}: Rs. ${normalizedValues[
            i
          ].toLocaleString("en-IN")}`;

          // Make hover easier: each month gets a wide hover band (not just a tiny point).
          const startXRaw = point.x - step / 2;
          const endXRaw = point.x + step / 2;
          const startX = Math.max(0, i === 0 ? 0 : startXRaw);
          const endX = Math.min(width, i === n - 1 ? width : endXRaw);
          const bandLeftPercent = (startX / width) * 100;
          const bandWidthPercent = ((endX - startX) / width) * 100;

          const anchorLeftPercent =
            ((point.x - startX) / Math.max(1, endX - startX)) * 100;
          const topPercent = ((160 - point.y) / 160) * 100;

          return (
            <div
              key={`${monthLabels[i]}-${i}`}
              className="group absolute top-0 h-full cursor-pointer"
              style={{
                left: `${bandLeftPercent}%`,
                width: `${bandWidthPercent}%`,
              }}
            >
              <div className="absolute inset-0 rounded-md group-hover:bg-purple-50/60" />

              <div
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${anchorLeftPercent}%`, top: `${topPercent}%` }}
              >
                <div className="h-8 w-8 rounded-full bg-transparent" />
                <div className="pointer-events-none absolute left-1/2 top-1/2 z-10 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#6A32A8] opacity-0 shadow-sm transition-opacity group-hover:opacity-100" />

                <div className="absolute bottom-full left-1/2 z-20 mb-2 hidden -translate-x-1/2 group-hover:block">
                  <div className="relative whitespace-nowrap rounded bg-gray-900 px-2 py-1 text-xs text-white">
                    {valueLabel}
                    <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                  </div>
                </div>

                <svg viewBox="0 0 32 32" className="absolute inset-0 h-8 w-8">
                  <circle cx="16" cy="16" r="14" fill="rgba(0,0,0,0.001)">
                    <title>{valueLabel}</title>
                  </circle>
                </svg>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const WorkerEditModal = ({
  isOpen,
  onClose,
  onSave,
  isSaving,
  initialValues,
  addAlert,
}) => {
  const [form, setForm] = React.useState({
    worker_name: "",
    contact: "",
    address: "",
  });

  React.useEffect(() => {
    if (!isOpen) return;
    setForm({
      worker_name: initialValues?.worker_name || "",
      contact: initialValues?.contact || "",
      address: initialValues?.address || "",
    });
  }, [isOpen, initialValues]);

  if (!isOpen) return null;

  const onContactChange = (value) => {
    const digitsOnly = String(value || "")
      .replace(/\D/g, "")
      .slice(0, 10);
    setForm((p) => ({ ...p, contact: digitsOnly }));
  };

  const handleSave = () => {
    const worker_name = String(form.worker_name || "").trim();
    const contact = String(form.contact || "").replace(/\D/g, "");
    const address = String(form.address || "").trim();

    if (!worker_name) {
      addAlert?.("Worker name is required", "error");
      return;
    }

    if (contact && contact.length !== 10) {
      addAlert?.("Contact number must be 10 digits", "error");
      return;
    }

    onSave({ worker_name, contact, address });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[92vw] max-w-lg">
        <h2 className="text-lg font-semibold mb-4">Edit Worker</h2>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md py-2 px-3"
              placeholder="Enter worker name"
              value={form.worker_name}
              onChange={(e) =>
                setForm((p) => ({ ...p, worker_name: e.target.value }))
              }
              disabled={isSaving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact No.
            </label>
            <input
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={10}
              className="w-full border border-gray-300 rounded-md py-2 px-3"
              placeholder="Enter 10 digit contact number"
              value={form.contact}
              onChange={(e) => onContactChange(e.target.value)}
              disabled={isSaving}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <textarea
              className="w-full border border-gray-300 rounded-md py-2 px-3 min-h-[90px]"
              placeholder="Enter address"
              value={form.address}
              onChange={(e) =>
                setForm((p) => ({ ...p, address: e.target.value }))
              }
              disabled={isSaving}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 rounded-md"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-[#6A32A8] text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const WorkerProfile = ({ userPermissions }) => {
  const navigate = useNavigate();
  const hasEditPermission =
    userPermissions?.edit_worker_management ||
    userPermissions?.is_admin ||
    userPermissions?.joined_as === "owner";
  const { addAlert } = useAlert();
  const { siteId, workerId } = useParams();
  const [activeTab, setActiveTab] = React.useState("attendance");

  // Dynamic data pulled to mirror AllWorkers.js calculations
  const [workerData, setWorkerData] = React.useState(null);
  const [workerName, setWorkerName] = React.useState("");
  const [showUpdateRateModal, setShowUpdateRateModal] = React.useState(false);
  const [workerPayment, setWorkerPayment] = React.useState(null);
  const [workerWageHistory, setWorkerWageHistory] = React.useState([]);
  const [dailyRate, setDailyRate] = React.useState(null);
  const [presentDays, setPresentDays] = React.useState(0);
  const [prevMonthPresent, setPrevMonthPresent] = React.useState(0);
  const [weeklyCounts, setWeeklyCounts] = React.useState([0, 0, 0, 0]);
  const [payments, setPayments] = React.useState([]);
  const [attendanceList, setAttendanceList] = React.useState([]);
  const [activeAttendanceTab, setActiveAttendanceTab] =
    React.useState("analytics");
  // State for month navigation in attendance history
  const [selectedMonth, setSelectedMonth] = React.useState(
    new Date().getMonth(),
  );
  const [selectedYear, setSelectedYear] = React.useState(
    new Date().getFullYear(),
  );
  const [paymentStats, setPaymentStats] = React.useState({
    totalPaid: 0,
    due: 0,
    monthlyPayments: [],
  });
  const [attendanceStats, setAttendanceStats] = React.useState({
    fullDays: 0,
    halfDays: 0,
    absentDays: 0,
    overtimeDays: 0,
    totalHours: 0,
    avgHoursPerDay: 0,
    efficiencyPercent: 0,
  });
  const [financeStats, setFinanceStats] = React.useState({
    currentTotal: 0,
    basicAmount: 0,
    overtimeAmount: 0,
    advanceAmount: 0,
    deductionAmount: 0,
    daysPresent: 0,
    daysAbsent: 0,
    totalWorkingDays: 0,
    monthlyTotalsThisYear: [],
    summaryWeek: 0,
    summaryMonth: 0,
    summaryQuarter: 0,
    summaryYear: 0,
    avgPerDayToDate: 0,
    effectiveHourly: 0,
    monthlyPotential: 0,
  });
  const [overtimeWageCurrentMonth, setOvertimeWageCurrentMonth] =
    React.useState(0);
  const [isDeletingWorker, setIsDeletingWorker] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [isUpdatingWorker, setIsUpdatingWorker] = React.useState(false);
  const [showEditWorkerModal, setShowEditWorkerModal] = React.useState(false);
  const [editWorkerInitialValues, setEditWorkerInitialValues] = React.useState({
    worker_name: "",
    contact: "",
    address: "",
  });
  const [showActionsMenu, setShowActionsMenu] = React.useState(false);
  const actionsMenuRef = React.useRef(null);
  const [downloadingPaymentId, setDownloadingPaymentId] = React.useState(null);

  const handleDeleteClick = () => {
    if (!hasEditPermission) {
      addAlert("You do not have permission to delete worker", "error");
      return;
    }
    setShowDeleteModal(true);
  };

  const handleEditWorkerClick = () => {
    if (!hasEditPermission) {
      addAlert("You do not have permission to edit worker", "error");
      return;
    }
    setEditWorkerInitialValues({
      worker_name: workerData?.worker_name || workerName || "",
      contact: workerData?.contact || "",
      address: workerData?.address || "",
    });
    setShowEditWorkerModal(true);
  };

  const deleteWorker = async () => {
    if (!workerId || isDeletingWorker) return;
    setIsDeletingWorker(true);
    try {
      const token = localStorage.getItem("access");
      const response = await axios.delete(
        `${BASE_URL}/worker/delete-worker/${workerId}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response?.data?.status) {
        addAlert("Worker deleted successfully", "success");
        navigate(`/site/${siteId}/workers`);
      } else {
        addAlert(response?.data?.message || "Failed to delete worker", "error");
      }
    } catch (error) {
      console.error("Failed to delete worker:", error);
      addAlert("Failed to delete worker. Please try again.", "error");
    } finally {
      setIsDeletingWorker(false);
      setShowDeleteModal(false);
    }
  };

  const updateWorker = async (values) => {
    if (!workerId || isUpdatingWorker) return;

    if (!hasEditPermission) {
      addAlert("You do not have permission to edit worker", "error");
      return;
    }

    const trimmedName = String(values?.worker_name || "").trim();
    const contactDigits = String(values?.contact || "").replace(/\D/g, "");
    const trimmedAddress = String(values?.address || "").trim();

    if (!trimmedName) {
      addAlert("Worker name is required", "error");
      return;
    }

    if (contactDigits && contactDigits.length !== 10) {
      addAlert("Contact number must be 10 digits", "error");
      return;
    }

    setIsUpdatingWorker(true);
    try {
      const token = localStorage.getItem("access");
      const payload = {
        name: trimmedName,
        contact: contactDigits,
        address: trimmedAddress,
      };

      const response = await axios.patch(
        `${BASE_URL}/worker/update-worker/${workerId}/`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (response?.data?.status) {
        setWorkerName(trimmedName);
        setWorkerData((prev) => ({
          ...(prev || {}),
          worker_name: trimmedName,
          contact: contactDigits,
          address: trimmedAddress,
        }));
        addAlert("Worker updated successfully", "success");
        setShowEditWorkerModal(false);
      } else {
        addAlert(response?.data?.message || "Failed to update worker", "error");
      }
    } catch (error) {
      console.error("Failed to update worker:", error);
      addAlert("Failed to update worker. Please try again.", "error");
    } finally {
      setIsUpdatingWorker(false);
    }
  };

  // Compute current month string and days in month (same logic as AllWorkers.js)
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonthIndex = now.getMonth();
  const currentMonthString = `${currentYear}-${String(
    currentMonthIndex + 1,
  ).padStart(2, "0")}`;
  const daysInCurrentMonth = new Date(
    currentYear,
    currentMonthIndex + 1,
    0,
  ).getDate();
  const prevMonthDate = new Date(currentYear, currentMonthIndex, 0); // last day of previous month
  const prevMonthString = `${prevMonthDate.getFullYear()}-${String(
    prevMonthDate.getMonth() + 1,
  ).padStart(2, "0")}`;

  React.useEffect(() => {
    // Fetch the worker list and pick the specific worker for name (keeps parity with list source)
    const fetchWorkerName = async () => {
      const site = localStorage.getItem("site");
      const accessToken = localStorage.getItem("access");
      try {
        const response = await fetch(`${BASE_URL}/worker/workers-page/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            worker_type: "Worker",
            sites: parseInt(site),
          }),
        });
        const data = await response.json();
        if (data?.status && Array.isArray(data?.data)) {
          const found = data.data.find(
            (w) => String(w.worker_id) === String(workerId),
          );
          if (found) {
            setWorkerData(found);
            setWorkerName(found.worker_name || "");
          }
        }
      } catch (_) {}
    };

    if (workerId) fetchWorkerName();
  }, [workerId]);

  React.useEffect(() => {
    if (!showActionsMenu) return;

    const onPointerDown = (e) => {
      if (!actionsMenuRef.current) return;
      if (!actionsMenuRef.current.contains(e.target)) {
        setShowActionsMenu(false);
      }
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowActionsMenu(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showActionsMenu]);

  const fetchRate = async () => {
    const accessToken = localStorage.getItem("access");
    try {
      const res = await fetch(
        `${BASE_URL}/worker/get-workerpayment-of-worker/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ worker_id: workerId }),
        },
      );
      const data = await res.json();
      if (data?.status && Array.isArray(data.data) && data.data.length > 0) {
        const activeWorkerPayment = data.data.filter(
          (d) => d.is_active === true,
        )[0];
        setWorkerWageHistory(data.data);
        setDailyRate(activeWorkerPayment?.amount);
        setWorkerPayment(activeWorkerPayment);
      } else {
        setDailyRate(null);
        setWorkerPayment(null);
      }
    } catch (_) {
      setDailyRate(null);
    }
  };
  useEffect(() => {
    if (workerId) fetchRate();
  }, [workerId]);

  const handleDownloadPaymentReceipt = async (payment) => {
    if (!payment || downloadingPaymentId === payment.id) return;

    setDownloadingPaymentId(payment.id);
    try {
      let organization = {};
      let siteData = {};

      try {
        const orgStr = localStorage.getItem("organization");
        if (orgStr) organization = JSON.parse(orgStr);
      } catch (_) {}

      try {
        const siteStr = localStorage.getItem("siteData");
        if (siteStr) siteData = JSON.parse(siteStr);
      } catch (_) {}

      const doc = (
        <WorkerPaymentReceiptPDFDocument
          transaction={payment}
          worker={{
            name: workerData?.worker_name || workerName || payment.worker_name,
            contact:
              workerData?.contact ||
              payment?.worker_phone ||
              payment?.worker_contact,
          }}
          organization={organization}
          siteData={siteData}
        />
      );

      const blob = await pdf(doc).toBlob();
      const txId =
        payment.transaction_id || String(payment.id ?? "").padStart(3, "0");
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Worker_Payment_Receipt_WORKER_PAY-${txId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download worker payment receipt:", error);
      addAlert("Failed to download receipt", "error");
    } finally {
      setDownloadingPaymentId(null);
    }
  };

  useEffect(() => {
    // Attendance history for current month -> present day count
    const fetchAttendance = async () => {
      const accessToken = localStorage.getItem("access");
      try {
        // Use all-months endpoint to compute current, previous and weekly breakdowns
        const res = await fetch(
          `${BASE_URL}/worker/get-a-worker-attendance/${workerId}/`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${accessToken}` },
          },
        );
        const data = await res.json();
        const list = Array.isArray(data?.data) ? data.data : [];
        setAttendanceList(list);
        const isPresent = (rec) =>
          String(rec?.attendance || "").toLowerCase() === "present";
        const getMonthKey = (iso) => iso?.slice(0, 7); // YYYY-MM

        // Current month present count
        const currCount = list.filter(
          (r) =>
            isPresent(r) &&
            getMonthKey(r.attendance_date) === currentMonthString,
        ).length;
        setPresentDays(currCount);

        // Previous month present count
        const prevCount = list.filter(
          (r) =>
            isPresent(r) && getMonthKey(r.attendance_date) === prevMonthString,
        ).length;
        setPrevMonthPresent(prevCount);

        // Weekly breakdown for current month (1-7, 8-14, 15-21, 22-end)
        const weekBuckets = [0, 0, 0, 0];
        // Attendance KPIs for current month
        let fullDays = 0;
        let halfDays = 0;
        let overtimeDays = 0;
        let totalHours = 0;
        let absentDays = 0;

        let overtimeWageCurr = 0;
        list.forEach((r) => {
          if (!isPresent(r)) {
            absentDays += 1;
            return;
          }
          if (getMonthKey(r.attendance_date) !== currentMonthString) return;
          const day = parseInt(r.attendance_date.slice(8, 10), 10);
          const idx = day <= 7 ? 0 : day <= 14 ? 1 : day <= 21 ? 2 : 3;
          weekBuckets[idx] += 1;

          const half = Boolean(r.half_day);
          const full = Boolean(r.full_day) || !half; // default present -> full day when flags missing
          const overtime = Math.max(0, parseFloat(r.overtime_hours) || 0);
          if (half) halfDays += 1;
          else if (full) fullDays += 1;
          if (overtime > 0) overtimeDays += 1;
          totalHours += (half ? 4 : 8) + overtime;
          overtimeWageCurr += r.overtime_wage ? parseFloat(r.overtime_wage) : 0;
        });
        setWeeklyCounts(weekBuckets);
        setOvertimeWageCurrentMonth(overtimeWageCurr);

        // Days elapsed in current month up to today
        const today = new Date();
        const daysElapsed =
          today.getFullYear() === currentYear &&
          today.getMonth() === currentMonthIndex
            ? Math.max(1, today.getDate())
            : daysInCurrentMonth;
        const avgHoursPerDay = totalHours / daysElapsed;
        const efficiencyPercent = (totalHours / (daysElapsed * 8)) * 100;
        setAttendanceStats({
          fullDays,
          halfDays,
          absentDays,
          overtimeDays,
          totalHours,
          avgHoursPerDay,
          efficiencyPercent,
        });
      } catch (_) {
        setPresentDays(0);
        setPrevMonthPresent(0);
        setWeeklyCounts([0, 0, 0, 0]);
        setAttendanceStats({
          fullDays: 0,
          halfDays: 0,
          overtimeDays: 0,
          totalHours: 0,
          avgHoursPerDay: 0,
          efficiencyPercent: 0,
        });
      }
    };

    if (workerId) fetchAttendance();
  }, [workerId, currentMonthString]);

  const attendancePercent = Math.round(
    (presentDays / daysInCurrentMonth) * 100,
  );
  const absenteeismRate = Math.max(0, 100 - attendancePercent);
  const totalPay = dailyRate != null ? dailyRate * presentDays : null;
  const changePercent =
    prevMonthPresent > 0
      ? Math.round(((presentDays - prevMonthPresent) / prevMonthPresent) * 100)
      : 0;
  const earningsPerHour = dailyRate != null ? Math.round(dailyRate / 8) : null;

  // Helper to get wage rate for a specific date
  const getRateForDate = React.useCallback(
    (dateStr) => {
      if (!workerWageHistory || workerWageHistory.length === 0)
        return dailyRate || 0;
      // Sort desc by start_date
      const sorted = [...workerWageHistory].sort(
        (a, b) => new Date(b.start_date) - new Date(a.start_date),
      );
      const target = new Date(dateStr);
      const found = sorted.find((w) => new Date(w.start_date) <= target);
      return found
        ? parseFloat(found.amount)
        : parseFloat(sorted[sorted.length - 1].amount) || 0;
    },
    [workerWageHistory, dailyRate],
  );

  // Compute finance numbers whenever dailyRate or attendanceStats change
  React.useEffect(() => {
    if (
      dailyRate == null &&
      (!workerWageHistory || workerWageHistory.length === 0)
    )
      return;

    const siteNow = new Date();
    const daysElapsed =
      siteNow.getFullYear() === currentYear &&
      siteNow.getMonth() === currentMonthIndex
        ? Math.max(1, siteNow.getDate())
        : daysInCurrentMonth;

    // Calculate current month basics using historical rates
    let currentBasic = 0;
    let currentDeduction = 0;

    // Filter attendance for current month
    const currentMonthRecords = attendanceList.filter((r) => {
      const d = r.attendance_date;
      return (
        d &&
        d.startsWith(currentMonthString) &&
        String(r.attendance).toLowerCase() === "present"
      );
    });

    currentMonthRecords.forEach((r) => {
      const rate = getRateForDate(r.attendance_date);
      currentBasic += rate;
      if (r.half_day) {
        currentDeduction += rate / 2;
      }
    });

    // Overtime comes from DB accumulation
    const overtimeAmount = overtimeWageCurrentMonth;
    const netCurrentTotal = currentBasic - currentDeduction + overtimeAmount;

    // Calendar stats
    const daysPresentCalc = attendanceStats.fullDays;
    const totalWorkingDays = daysInCurrentMonth;
    const daysAbsent = Math.max(0, daysElapsed - daysPresentCalc);

    // Compute Year Totals using existing attendanceList and history
    const computeYearTotals = () => {
      const months = Array.from(
        { length: 12 },
        (_, i) => `${currentYear}-${String(i + 1).padStart(2, "0")}`,
      );

      const totals = months.map((m) => {
        // build earnings for month m
        let monthTotal = 0;
        attendanceList.forEach((r) => {
          if (
            r.attendance_date?.startsWith(m) &&
            String(r.attendance).toLowerCase() === "present"
          ) {
            const rate = getRateForDate(r.attendance_date);
            const half = Boolean(r.half_day);
            // Base wage for day
            monthTotal += half ? rate / 2 : rate;
            // Add overtime
            monthTotal += r.overtime_wage ? parseFloat(r.overtime_wage) : 0;
          }
        });
        return Math.round(monthTotal);
      });

      // Summary earnings (week/month/quarter/year)
      const weekEarnings = Math.round(
        // approximate: last 7 days of current month
        (() => {
          const today = new Date();
          const start = new Date(
            today.getFullYear(),
            today.getMonth(),
            Math.max(1, today.getDate() - 6),
          );
          let total = 0;
          attendanceList.forEach((r) => {
            const d = new Date(r.attendance_date);
            if (
              d >= start &&
              d <= today &&
              String(r.attendance).toLowerCase() === "present"
            ) {
              const rate = getRateForDate(r.attendance_date);
              const half = Boolean(r.half_day);
              total += half ? rate / 2 : rate;
              total += r.overtime_wage ? parseFloat(r.overtime_wage) : 0;
            }
          });
          return total;
        })(),
      );

      const monthEarnings = Math.round(netCurrentTotal);
      const quarterMonths = [
        currentMonthIndex - 2,
        currentMonthIndex - 1,
        currentMonthIndex,
      ].filter((i) => i >= 0);
      const quarterEarnings = quarterMonths.reduce(
        (sum, idx) => sum + (totals[idx] || 0),
        0,
      );
      const yearEarnings = totals.reduce((a, b) => a + b, 0);

      const avgPerDayToDate = netCurrentTotal / daysElapsed;

      setFinanceStats({
        currentTotal: Math.round(netCurrentTotal),
        basicAmount: Math.round(currentBasic),
        overtimeAmount: Math.round(overtimeAmount),
        advanceAmount: 0,
        deductionAmount: Math.round(currentDeduction),
        daysPresent: daysPresentCalc,
        daysAbsent,
        totalWorkingDays,
        monthlyTotalsThisYear: totals,
        summaryWeek: weekEarnings,
        summaryMonth: monthEarnings,
        summaryQuarter: Math.round(quarterEarnings),
        summaryYear: Math.round(yearEarnings),
        avgPerDayToDate,
        effectiveHourly: dailyRate ? dailyRate / 8 : 0, // Still uses current rate as reference
        monthlyPotential: (dailyRate || 0) * totalWorkingDays,
      });
    };

    computeYearTotals();
  }, [
    dailyRate,
    attendanceStats,
    workerId,
    presentDays,
    overtimeWageCurrentMonth,
    workerWageHistory,
    attendanceList,
    getRateForDate,
  ]);

  React.useEffect(() => {
    const fetchPayments = async () => {
      const site = localStorage.getItem("site");
      const accessToken = localStorage.getItem("access");
      try {
        const res = await fetch(
          `${BASE_URL}/finance/get-all-worker-transaction-of-a-site/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({ sites: parseInt(site) }),
          },
        );
        const data = await res.json();
        if (data?.status && Array.isArray(data.data)) {
          // Filter payments for this worker
          const workerPayments = data.data.filter(
            (p) => String(p.worker_id) === String(workerId),
          );
          setPayments(workerPayments);

          // Calculate total paid
          const totalPaid = workerPayments.reduce(
            (sum, p) => sum + (parseFloat(p.amount) || 0),
            0,
          );

          // Calculate monthly payments for chart (current year)
          const monthlyTotals = Array(12).fill(0);
          workerPayments.forEach((p) => {
            const date = new Date(p.transaction_date);
            if (date.getFullYear() === currentYear) {
              monthlyTotals[date.getMonth()] += parseFloat(p.amount) || 0;
            }
          });

          setPaymentStats({
            totalPaid: Math.round(totalPaid),
            due: Math.max(0, financeStats.currentTotal - totalPaid),
            monthlyPayments: monthlyTotals,
          });
        }
      } catch (_) {
        setPayments([]);
      }
    };

    if (workerId) fetchPayments();
  }, [workerId, financeStats.currentTotal]);

  const updatePaymentRate = async (newRate) => {
    const accessToken = localStorage.getItem("access");
    try {
      const res = await fetch(
        `${BASE_URL}/worker/update-workerpayment/${workerPayment?.id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            amount: newRate,
          }),
        },
      );
      const data = await res.json();
      if (data?.status) {
        setDailyRate(newRate);
        await fetchRate();
        setShowUpdateRateModal(false);
        addAlert("Payment rate updated successfully", "success");
        return true;
      }
      return false;
    } catch (_) {
      addAlert("Failed to update payment rate", "error");
      return false;
    }
  };

  const addPaymentRate = async (newRate) => {
    const accessToken = localStorage.getItem("access");
    try {
      const res = await fetch(`${BASE_URL}/worker/create-workerpayment/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          worker_id: workerId,
          payment_type: "Daily wage",
          amount: newRate,
        }),
      });
      const data = await res.json();
      if (data?.status) {
        setDailyRate(newRate);
        await fetchRate();
        setShowUpdateRateModal(false);
        addAlert("Payment rate added successfully", "success");
        return true;
      }
      return false;
    } catch (_) {
      addAlert("Failed to add payment rate", "error");
      return false;
    }
  };

  const WorkerPaymentRateModal = ({ isOpen, onClose, onSave, initialRate }) => {
    const [rateInput, setRateInput] = React.useState(initialRate || "");

    useEffect(() => {
      setRateInput(initialRate || "");
    }, [initialRate]);

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-80">
          <h2 className="text-lg font-semibold mb-4">
            {initialRate != null ? "Update" : "Add"} Daily Rate
          </h2>
          <div className="relative mb-4">
            <span className=" text-gray-500 text-lg mr-2">₹</span>
            <input
              type="number"
              className="w-[90%] border border-gray-300 rounded-md py-1 px-2 "
              placeholder="Enter daily rate"
              value={rateInput}
              onChange={(e) => setRateInput(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 rounded-md"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                const rateValue = parseFloat(rateInput);
                if (!isNaN(rateValue) && rateValue > 0) {
                  onSave(rateValue);
                }
              }}
              className="px-4 py-2 bg-[#6A32A8] text-white rounded-md"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-gray-100 w-full min-h-screen p-6 md:p-8">
      <div className="mx-auto max-w-[88rem] space-y-4">
        <div className="bg-white rounded-2xl shadow-sm px-3 pb-5 pt-4 md:px-6 md:pb-4 md:pt-2 border-2 border-[#8067E0]">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/site/${siteId}/workers`)}
                className="w-8 h-8 flex items-center justify-center text-[#232323] -ml-2 md:-ml-3"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                {/* <User className="w-5 h-5 text-[#6A32A8]" /> */}
                <Icon path={mdiAccountHardHat} size={1.2} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold">{workerName || "—"}</p>
                  {/* <span className="text-[14px] px-2 py-0.5 rounded-full bg-[#EFE8F6] text-[#6A32A8]">
                    Worker
                  </span> */}
                  <span className="text-[14px] px-2 py-0.5 rounded-full bg-[#EFE8F6] text-[#6A32A8]">
                    {workerData?.worker_workname}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  {dailyRate != null ? (
                    <>
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#EEF2FF] text-[#4F46E5] border border-[#E0E7FF] rounded-full text-xs font-semibold shadow-sm">
                        <IndianRupee className="w-3.5 h-3.5" />
                        <span className="text-sm font-medium">
                          {dailyRate}/day
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 font-medium">
                        Effective from {workerPayment?.start_date || "—"}
                      </span>
                    </>
                  ) : (
                    <span className="text-xs text-gray-400 italic">
                      — No Active Wage Set —
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                className="px-3 py-2 bg-[#F7F4FA] text-sm rounded-lg border-2 border-[#3B0069] hover:bg-[#EDE7F4]"
                onClick={() => {
                  if (!hasEditPermission) {
                    addAlert(
                      "You do not have permission to edit worker rate",
                      "error",
                    );
                    return;
                  }
                  setShowUpdateRateModal(true);
                }}
              >
                {dailyRate === null ? "Add Daily Rate" : "Edit Daily Rate"}
              </button>
              <div className="relative" ref={actionsMenuRef}>
                <button
                  type="button"
                  className="p-2 bg-white text-[#232323] rounded-lg border-2 border-gray-300 hover:bg-gray-50"
                  onClick={() => setShowActionsMenu((v) => !v)}
                  title="Worker actions"
                  aria-label="Worker actions"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>

                {showActionsMenu && (
                  <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden">
                    <button
                      type="button"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => {
                        setShowActionsMenu(false);
                        handleEditWorkerClick();
                      }}
                      disabled={!hasEditPermission || isUpdatingWorker}
                    >
                      <Pencil className="w-4 h-4" />
                      Edit
                    </button>
                    <button
                      type="button"
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => {
                        setShowActionsMenu(false);
                        handleDeleteClick();
                      }}
                      disabled={!hasEditPermission || isDeletingWorker}
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
              <div className="mr-8 text-sm">
                <p>
                  <span className="text-[#232323]">Contact No:</span>{" "}
                  {workerData?.contact || "—"}
                </p>
                <p>
                  <span className="text-[#232323]">Address:</span>{" "}
                  {workerData?.address || "—"}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-400 mt-3 pt-3" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <div className="border rounded-xl p-3">
              <p className="text-[14px] text-gray-500">
                Days Present (This Month)
              </p>
              <p className="text-[16px] font-medium text-[#232323]">
                <span className="text-2xl font-semibold">
                  {presentDays}/{daysInCurrentMonth}
                </span>{" "}
                days
              </p>
            </div>
            <div className="border rounded-xl p-3">
              <p className="text-[14px] text-gray-500">
                Attendance Rate (This month)
              </p>
              <p className="text-2xl font-semibold text-[#232323]">
                {isNaN(attendancePercent) ? "—" : `${attendancePercent} %`}
              </p>
            </div>
            <div className="border rounded-xl p-3">
              <p className="text-[14px] text-gray-500">
                Total Earning (This month)
              </p>
              <p className="text-2xl font-semibold text-[#232323]">
                ₹{" "}
                {Math.round(financeStats.currentTotal).toLocaleString("en-IN")}
              </p>
            </div>
            <div className="border rounded-xl p-3">
              <p className="text-[14px] text-gray-500">Total Paid</p>
              <p className="text-2xl font-semibold text-[#232323]">
                {isNaN(paymentStats.totalPaid)
                  ? "—"
                  : `₹ ${paymentStats.totalPaid.toLocaleString("en-IN")}`}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-4 md:p-3 flex items-center justify-between">
          <button className="inline-flex items-center bg-[#F7F4FA] rounded-xl px-3 py-2 text-sm border-2 border-[#3B0069]">
            {(() => {
              const d = new Date();
              const day = d.getDate();
              const month = d.toLocaleString(undefined, { month: "long" });
              const year = d.getFullYear();
              return (
                <>
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  <CalendarDays className="w-4 h-4 mr-2" />{" "}
                  {`Today, ${day} ${month}, ${year}`}
                </>
              );
            })()}
          </button>
          <div className="flex items-center gap-2">
            {/* <button className="text-sm px-3 py-2 rounded-lg bg-[#F7F4FA]">
              Full day
            </button>
            <button className="text-sm px-3 py-2 rounded-lg bg-[#F7F4FA]">
              Overtime hours
            </button> */}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm">
          <div className="px-2 pt-3">
            <div className="flex flex-row justify-between px-16 items-center text-sm w-full">
              {/* <button
                onClick={() => setActiveTab("performance")}
                className={`justify-self-start pb-2 ml-28 flex flex-col items-center ${
                  activeTab === "performance"
                    ? "text-[#1C1B1F] font-semibold"
                    : "text-[#8F8F8F]"
                }`}
              >
                <span>Performance</span>
                {activeTab === "performance" && (
                  <span className="block mt-2 h-[2px] w-28 bg-[#1C1B1F] rounded-full" />
                )}
              </button> */}
              <button
                onClick={() => setActiveTab("attendance")}
                className={`justify-self-center pb-2 flex flex-col items-center ${
                  activeTab === "attendance"
                    ? "text-[#1C1B1F] font-semibold border-b-2 border-[#1C1B1F]"
                    : "text-[#8F8F8F]"
                }`}
              >
                <span>Attendance</span>
              </button>
              <button
                onClick={() => setActiveTab("finance")}
                className={`justify-self-end pb-2 flex flex-col items-center ${
                  activeTab === "finance"
                    ? "text-[#1C1B1F] font-semibold border-b-2 border-[#1C1B1F]"
                    : "text-[#8F8F8F]"
                }`}
              >
                <span>Finance</span>
              </button>
              <button
                onClick={() => setActiveTab("payments")}
                className={`justify-self-end pb-2 flex flex-col items-center ${
                  activeTab === "payments"
                    ? "text-[#1C1B1F] font-semibold border-b-2 border-[#1C1B1F]"
                    : "text-[#8F8F8F]"
                }`}
              >
                <span>Payments</span>
              </button>
            </div>
          </div>

          {activeTab === "performance" && (
            <div className="grid grid-cols-1 gap-6 px-5 pb-5 pt-2">
              {/* Performance Analytics container */}
              <div className="border border-[#1C1B1F] rounded-2xl p-3">
                <p className="text-base font-medium mb-3">
                  Performance Analysis
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-[#F4EFFA] rounded-xl p-4">
                    <p className="text-sm font-medium text-[#3B0069]">
                      Overall Performance
                    </p>
                    <div className="text-3xl font-bold mt-2">
                      {isNaN(attendancePercent) ? "—" : `${attendancePercent}%`}
                    </div>
                    <p
                      className={`text-sm mt-1 ${
                        changePercent >= 0 ? "text-[#0B8F1A]" : "text-red-600"
                      }`}
                    >
                      This Month {changePercent >= 0 ? "+" : ""}
                      {changePercent}%
                    </p>
                  </div>

                  <div className="flex items-center justify-center">
                    <PerformanceRing
                      percent={isNaN(attendancePercent) ? 0 : attendancePercent}
                    />
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-8">
                  <div className="border rounded-xl p-3">
                    <p className="text-[14px] text-gray-500">Earnings/Hour</p>
                    <p className="font-semibold text-2xl">
                      {earningsPerHour != null ? `₹ ${earningsPerHour}` : "—"}
                    </p>
                  </div>
                  <div className="border rounded-xl p-3">
                    <p className="text-[14px] text-gray-500">Rank</p>
                    <p className="font-base text-[#232323]">
                      <span className="text-2xl font-semibold">#12</span> of 50
                    </p>
                  </div>
                  <div className="border rounded-xl p-3">
                    <p className="text-[14px] text-gray-500">
                      Earning till date
                    </p>
                    <p className="font-semibold text-2xl">₹ 51,400</p>
                  </div>
                </div>

                <div className="mt-4">
                  <p className="text-base font-medium mb-3">
                    Performance Trend
                  </p>
                  <div className="grid grid-cols-4 gap-3">
                    {(() => {
                      const max = Math.max(1, ...weeklyCounts);
                      const bars = weeklyCounts.map((v, i) => {
                        const h = 20 + Math.round((v / max) * 80); // 20-100
                        const active = i === weeklyCounts.indexOf(max);
                        return <MiniBar key={i} height={h} active={active} />;
                      });
                      return bars;
                    })()}
                  </div>
                  <div className="mt-2 grid grid-cols-4 text-[14px] text-gray-500">
                    <span>Week 1</span>
                    <span>Week 2</span>
                    <span>Week 3</span>
                    <span>Week 4</span>
                  </div>
                </div>
              </div>

              {/* Performance Pattern container */}
              <div className="border border-[#1C1B1F] rounded-2xl p-3">
                <p className="text-base font-medium mb-2">
                  Performance Pattern
                </p>
                <div className="rounded-xl p-3">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span>
                      <span className="font-semibold">85%</span>{" "}
                      <span className="text-[#0B8F1A] text-sm">+5%</span>
                    </span>
                  </div>
                  <PerformancePattern values={weeklyCounts} />
                </div>
              </div>
            </div>
          )}

          {activeTab === "attendance" && (
            <div className="flex flex-col">
              {/* tabs named analytics and history */}
              <div className="flex w-full flex-row items-center justify-center gap-12 border-b-2 border-gray-100 bg-gradient-to-r from-purple-50/30 to-white py-1">
                <button
                  onClick={() => setActiveAttendanceTab("analytics")}
                  className={`pb-3 pt-2 px-6 text-sm font-medium transition-all duration-200 relative ${
                    activeAttendanceTab === "analytics"
                      ? "text-[#6A32A8] font-semibold"
                      : "text-[#8F8F8F] hover:text-[#6A32A8]"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                    Analytics
                  </span>
                  {activeAttendanceTab === "analytics" && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#6A32A8] to-[#8F6AD6] rounded-full" />
                  )}
                </button>
                <button
                  onClick={() => setActiveAttendanceTab("history")}
                  className={`pb-3 pt-2 px-6 text-sm font-medium transition-all duration-200 relative ${
                    activeAttendanceTab === "history"
                      ? "text-[#6A32A8] font-semibold"
                      : "text-[#8F8F8F] hover:text-[#6A32A8]"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    History
                  </span>
                  {activeAttendanceTab === "history" && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#6A32A8] to-[#8F6AD6] rounded-full" />
                  )}
                </button>
              </div>

              {activeAttendanceTab === "analytics" ? (
                <div className="p-8 space-y-6">
                  <p className="text-[16px] font-medium text-[#232323]">
                    Detailed Attendance Analysis
                  </p>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="border rounded-xl px-3 py-2">
                      <p className="text-[14px] text-gray-500">Full Days</p>
                      <p className="text-2xl font-semibold">
                        {attendanceStats.fullDays}
                      </p>
                    </div>
                    <div className="border rounded-xl px-3 py-2">
                      <p className="text-[14px] text-gray-500">Half Days</p>
                      <p className="text-2xl font-semibold">
                        {attendanceStats.halfDays}
                      </p>
                    </div>
                    <div className="border rounded-xl px-3 py-2">
                      <p className="text-[14px] text-gray-500">Overtime Days</p>
                      <p className="text-2xl font-semibold">
                        {attendanceStats.overtimeDays}
                      </p>
                    </div>
                    {/* <div className="border rounded-xl px-3 py-2">
                  <p className="text-[14px] text-gray-500">Consistency</p>
                  <span className="text-[14px] px-2 py-1 rounded-full bg-[#EFE8F6] text-[#6A32A8] inline-block mt-1">
                    Present
                  </span>
                </div> */}
                    <div className="border rounded-xl px-3 py-2">
                      <p className="text-[14px] text-gray-500">Absent Days</p>
                      <p className="text-2xl font-semibold">
                        {attendanceStats.absentDays}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="border rounded-xl px-4 py-2">
                      <p className="text-base font-medium mb-3">
                        Attendance Distribution
                      </p>
                      <div className="flex items-center gap-6">
                        <AttendancePie
                          fullCount={attendanceStats.fullDays}
                          halfCount={attendanceStats.halfDays}
                          overtimeCount={attendanceStats.overtimeDays}
                        />
                        <div className="text-[14px] space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-sm bg-[#6A32A8]" />
                            <span>{attendanceStats.fullDays} (Full Day)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-sm bg-[#BBA6E9]" />
                            <span>{attendanceStats.halfDays} (Half Day)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-sm bg-[#C9B7F1]" />
                            <span>
                              {attendanceStats.overtimeDays} (Overtime Days)
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Dynamic Monthly Trend - Last 4 months */}
                    <div className="border rounded-xl px-4 py-2">
                      <p className="text-base font-medium mb-3">
                        Monthly Trend
                      </p>
                      <div className="flex items-end justify-between h-40">
                        {(() => {
                          // Get last 4 months (including current)
                          const months = [];
                          const today = new Date();
                          for (let i = 3; i >= 0; i--) {
                            const d = new Date(
                              today.getFullYear(),
                              today.getMonth() - i,
                              1,
                            );
                            months.push({
                              name: d.toLocaleString("default", {
                                month: "short",
                              }),
                              year: d.getFullYear(),
                              monthIndex: d.getMonth(),
                              fullYear: d.getFullYear(),
                            });
                          }

                          // Compute present days per month from attendance list
                          const monthlyPresent = months.map((m) => {
                            const key = `${m.fullYear}-${String(
                              m.monthIndex + 1,
                            ).padStart(2, "0")}`;
                            return attendanceList.filter(
                              (r) =>
                                String(r.attendance || "").toLowerCase() ===
                                  "present" &&
                                r.attendance_date?.startsWith(key),
                            ).length;
                          });

                          const maxPresent = Math.max(1, ...monthlyPresent);
                          return monthlyPresent.map((count, i) => {
                            const height =
                              40 + Math.round((count / maxPresent) * 100);
                            const isCurrent = i === 3;
                            return (
                              <div
                                key={months[i].name}
                                className="relative group flex-1 flex justify-center"
                              >
                                <MonthBar height={height} active={isCurrent} />
                                {/* Tooltip */}
                                <div className="absolute bottom-full mb-2 hidden group-hover:block">
                                  <div className="bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                                    {count} {count === 1 ? "day" : "days"}
                                    <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                                  </div>
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                      <div className="mt-2 grid grid-cols-4 text-[14px] text-gray-500">
                        {(() => {
                          const monthNames = [];
                          for (let i = 3; i >= 0; i--) {
                            const d = new Date();
                            d.setMonth(d.getMonth() - i);
                            monthNames.push(
                              d.toLocaleString("default", { month: "short" }),
                            );
                          }
                          return monthNames.map((name) => (
                            <span key={name}>{name}</span>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="border rounded-xl px-3 py-2">
                      <p className="text-[14px] text-gray-500">Total Hours</p>
                      <p className="text-2xl font-semibold">
                        {attendanceStats.totalHours.toFixed(1)} h
                      </p>
                    </div>
                    <div className="border rounded-xl px-3 py-2">
                      <p className="text-[14px] text-gray-500">Avg Hours/day</p>
                      <p className="text-2xl font-semibold">
                        {attendanceStats.avgHoursPerDay.toFixed(1)} h
                      </p>
                    </div>
                    <div className="border rounded-xl px-3 py-2">
                      <p className="text-[14px] text-gray-500">Efficiency</p>
                      <p className="text-2xl font-semibold">
                        {attendanceStats.efficiencyPercent.toFixed(1)} %
                      </p>
                    </div>
                  </div>
                </div>
              ) : activeAttendanceTab === "history" ? (
                <div className="p-8 space-y-6">
                  {/* Month Navigation */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => {
                          const newDate = new Date(
                            selectedYear,
                            selectedMonth - 1,
                            1,
                          );
                          setSelectedMonth(newDate.getMonth());
                          setSelectedYear(newDate.getFullYear());
                        }}
                        className="p-2 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-700 transition-colors"
                        aria-label="Previous month"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </button>

                      <div className="text-center min-w-[200px]">
                        <p className="text-xl font-semibold text-[#6A32A8]">
                          {new Date(
                            selectedYear,
                            selectedMonth,
                          ).toLocaleDateString("en-US", {
                            month: "long",
                            year: "numeric",
                          })}
                        </p>
                      </div>

                      <button
                        onClick={() => {
                          const newDate = new Date(
                            selectedYear,
                            selectedMonth + 1,
                            1,
                          );
                          const today = new Date();
                          // Don't allow going beyond current month
                          if (newDate <= today) {
                            setSelectedMonth(newDate.getMonth());
                            setSelectedYear(newDate.getFullYear());
                          }
                        }}
                        disabled={
                          selectedMonth === new Date().getMonth() &&
                          selectedYear === new Date().getFullYear()
                        }
                        className="p-2 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Next month"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>

                    <button
                      onClick={() => {
                        setSelectedMonth(new Date().getMonth());
                        setSelectedYear(new Date().getFullYear());
                      }}
                      className="px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                    >
                      Current Month
                    </button>
                  </div>

                  {(() => {
                    // Filter attendance by selected month and year
                    const monthKey = `${selectedYear}-${String(selectedMonth + 1).padStart(2, "0")}`;
                    const filteredByMonth = attendanceList.filter((record) => {
                      return record.attendance_date?.startsWith(monthKey);
                    });

                    return filteredByMonth.length > 0 ? (
                      <div className="border rounded-xl overflow-hidden">
                        <div className="bg-gradient-to-r from-purple-50 to-purple-100/50 px-6 py-3 border-b">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-purple-900">
                              {filteredByMonth.length}{" "}
                              {filteredByMonth.length === 1
                                ? "record"
                                : "records"}{" "}
                              found
                            </p>
                            <div className="flex items-center gap-4 text-xs text-purple-700">
                              <span className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                Present:{" "}
                                {
                                  filteredByMonth.filter(
                                    (r) =>
                                      String(
                                        r.attendance || "",
                                      ).toLowerCase() === "present",
                                  ).length
                                }
                              </span>
                              <span className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                Absent:{" "}
                                {
                                  filteredByMonth.filter(
                                    (r) =>
                                      String(
                                        r.attendance || "",
                                      ).toLowerCase() === "absent",
                                  ).length
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                          <table className="w-full">
                            <thead className="bg-gradient-to-r from-[#F7F4FA] to-[#EFE8F6] sticky top-0">
                              <tr>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-[#6A32A8] uppercase tracking-wider">
                                  Date
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-[#6A32A8] uppercase tracking-wider">
                                  Marked
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-[#6A32A8] uppercase tracking-wider">
                                  Day
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-[#6A32A8] uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="px-6 py-4 text-left text-xs font-semibold text-[#6A32A8] uppercase tracking-wider">
                                  Overtime
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                              {(() => {
                                // Sort by date descending (most recent first)
                                const sorted = [...filteredByMonth].sort(
                                  (a, b) => {
                                    const dateA = new Date(
                                      a.attendance_date || 0,
                                    );
                                    const dateB = new Date(
                                      b.attendance_date || 0,
                                    );
                                    return dateB - dateA;
                                  },
                                );

                                return sorted.map((record, index) => {
                                  const date = new Date(record.attendance_date);
                                  const isValid = !isNaN(date.getTime());
                                  const formattedDate = isValid
                                    ? date.toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })
                                    : "—";
                                  const dayName = isValid
                                    ? date.toLocaleDateString("en-US", {
                                        weekday: "short",
                                      })
                                    : "—";

                                  const status = String(
                                    record.attendance || "",
                                  ).toLowerCase();
                                  const hours = record.overtime_hours || 0;
                                  const halfDay = record.half_day || false;
                                  let statusBadge = {
                                    text: "Unknown",
                                    className: "bg-gray-100 text-gray-800",
                                  };

                                  if (status === "present") {
                                    statusBadge = {
                                      text: "Present",
                                      className:
                                        "bg-green-100 text-green-800 border border-green-200",
                                    };
                                  } else if (status === "absent") {
                                    statusBadge = {
                                      text: "Absent",
                                      className:
                                        "bg-red-100 text-red-800 border border-red-200",
                                    };
                                  } else if (status === "half day") {
                                    statusBadge = {
                                      text: "Half Day",
                                      className:
                                        "bg-yellow-100 text-yellow-800 border border-yellow-200",
                                    };
                                  } else if (status === "overtime") {
                                    statusBadge = {
                                      text: "Overtime",
                                      className:
                                        "bg-purple-100 text-purple-800 border border-purple-200",
                                    };
                                  }

                                  return (
                                    <tr
                                      key={index}
                                      className="hover:bg-purple-50/30 transition-colors duration-150"
                                    >
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                        {formattedDate}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        {record.created_on
                                          ? new Date(
                                              record.created_on,
                                            ).toLocaleTimeString("en-IN", {
                                              hour: "2-digit",
                                              minute: "2-digit",
                                              hour12: true,
                                            })
                                          : "—"}
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                        {dayName}
                                      </td>
                                      <td className="px-4 py-4 whitespace-nowrap gap-2">
                                        <div className="flex flex-row items-center gap-1">
                                          <span
                                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${statusBadge.className}`}
                                          >
                                            {statusBadge.text}
                                          </span>
                                          {halfDay && (
                                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
                                              Half Day
                                            </span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {hours > 0 ? `${hours} hrs` : "—"}
                                      </td>
                                    </tr>
                                  );
                                });
                              })()}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                          <svg
                            className="w-8 h-8 text-purple-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <p className="text-gray-500 text-lg font-medium mb-1">
                          No Records for{" "}
                          {new Date(
                            selectedYear,
                            selectedMonth,
                          ).toLocaleDateString("en-US", {
                            month: "long",
                            year: "numeric",
                          })}
                        </p>
                        <p className="text-gray-400 text-sm">
                          Try selecting a different month
                        </p>
                      </div>
                    );
                  })()}
                </div>
              ) : null}
            </div>
          )}

          {activeTab === "finance" && (
            <div className="p-8 space-y-6">
              {/* Current Month Total - separate section */}
              <div className="border rounded-2xl px-4 py-3">
                <div className="space-y-4">
                  <div>
                    <p className="text-[14px] text-gray-500">
                      Current Month Total
                    </p>
                    <p className="text-2xl font-semibold">
                      ₹{" "}
                      {Math.round(financeStats.currentTotal).toLocaleString(
                        "en-IN",
                      )}
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-[14px]">
                    <div className="border rounded-lg px-3 py-2">
                      <p className="text-gray-500">Days Present</p>
                      <p className="font-semibold text-2xl">
                        {financeStats.daysPresent}
                      </p>
                    </div>
                    <div className="border rounded-lg px-3 py-2">
                      <p className="text-gray-500">Days Absent</p>
                      <p className="font-semibold text-2xl">
                        {financeStats.daysAbsent}
                      </p>
                    </div>
                    <div className="border rounded-lg px-3 py-2">
                      <p className="text-gray-500">Total Working Days</p>
                      <p className="font-semibold text-2xl">
                        {financeStats.totalWorkingDays}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Breakdown - distribution + amounts + detailed breakdown */}
              <div className="border rounded-2xl p-4">
                <p className="text-base font-medium mb-2">Payment Breakdown</p>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium mb-2">Distribution</p>
                    <div className="flex items-center gap-6">
                      {(() => {
                        const total = Math.max(
                          1,
                          financeStats.basicAmount +
                            financeStats.overtimeAmount +
                            financeStats.deductionAmount,
                        );
                        const basic = financeStats.basicAmount / total;
                        const overtime = financeStats.overtimeAmount / total;
                        const deduction = financeStats.deductionAmount / total;
                        return (
                          <FinanceDonut
                            basic={basic}
                            overtime={overtime}
                            advance={0}
                            deduction={deduction}
                            values={{
                              basic: financeStats.basicAmount,
                              overtime: financeStats.overtimeAmount,
                              deduction: financeStats.deductionAmount,
                            }}
                          />
                        );
                      })()}
                      <div className="text-[14px] space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-sm bg-[#C9B7F1]" />
                          <span>
                            Basic: ₹{" "}
                            {financeStats.basicAmount.toLocaleString("en-IN")}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-sm bg-[#6A32A8]" />
                          <span>
                            Overtime: ₹{" "}
                            {financeStats.overtimeAmount.toLocaleString(
                              "en-IN",
                            )}
                          </span>
                        </div>
                        {/* <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-sm bg-[#8F6AD6]" />
                          <span>
                            Advance: ₹{" "}
                            {financeStats.advanceAmount.toLocaleString("en-IN")}
                          </span>
                        </div> */}
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-2">Amounts</p>
                    <div className="flex items-end justify-between h-40">
                      {(() => {
                        const vals = [
                          financeStats.basicAmount,
                          financeStats.overtimeAmount,
                          financeStats.deductionAmount,
                        ];
                        const labels = [
                          "Basic Salary",
                          "Overtime",
                          "Deduction",
                        ];
                        const max = Math.max(1, ...vals);
                        return vals.map((v, i) => (
                          <AmountBar
                            key={labels[i]}
                            height={30 + Math.round((v / max) * 120)}
                            active={v === max}
                            label={labels[i]}
                            value={v}
                          />
                        ));
                      })()}
                    </div>
                    <div className="mt-2 grid grid-cols-3 text-[14px] text-gray-500">
                      <span>Basic Salary</span>
                      <span>Overtime</span>
                      <span>Deduction</span>
                    </div>
                  </div>
                </div>

                <div className="mt-5">
                  <p className="text-base font-medium mb-2">
                    Detailed Breakdown
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-[14px]">
                    <span className="px-4 py-2 rounded-lg border">
                      Basic Salary{" "}
                      <span className="font-semibold ml-4">
                        ₹ {financeStats.basicAmount.toLocaleString("en-IN")}
                      </span>
                    </span>
                    <span className="px-4 py-2 rounded-lg border">
                      Overtime{" "}
                      <span className="font-semibold ml-4">
                        ₹ {financeStats.overtimeAmount.toLocaleString("en-IN")}
                      </span>
                    </span>
                    <span className="px-4 py-2 rounded-lg border">
                      Deduction{" "}
                      <span className="font-semibold ml-4">
                        ₹ {financeStats.deductionAmount.toLocaleString("en-IN")}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Payment Trends - stacked below */}
              <div className="border rounded-2xl px-4 py-3">
                <p className="text-base font-medium mb-2">Payment Trends</p>
                <div className="border rounded-xl p-3">
                  <FinanceTrend values={financeStats.monthlyTotalsThisYear} />
                  <div className="mt-2 grid grid-cols-12 text-[14px] px-4 text-gray-500 gap-x-2">
                    <span>Jan</span>
                    <span>Feb</span>
                    <span>Mar</span>
                    <span>Apr</span>
                    <span>May</span>
                    <span>Jun</span>
                    <span>Jul</span>
                    <span>Aug</span>
                    <span>Sep</span>
                    <span>Oct</span>
                    <span>Nov</span>
                    <span>Dec</span>
                  </div>
                </div>
              </div>

              {/* Summaries and wage analysis together in a separate box */}
              <div className="border rounded-2xl px-4 py-3 space-y-4">
                <p className="text-base font-medium">
                  Total Calculations & Summaries
                </p>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="border rounded-xl p-4">
                    <p className="text-sm font-medium mb-2">This Week</p>
                    <div className="text-[14px] text-gray-500 space-y-1">
                      <div className="flex items-center justify-between">
                        <span>Earnings</span>
                        <span className="font-semibold text-gray-800">
                          ₹ {financeStats.summaryWeek.toLocaleString("en-IN")}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Avg/Day</span>
                        <span className="font-semibold text-gray-800">
                          ₹{" "}
                          {Math.round(
                            financeStats.currentTotal / 7,
                          ).toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="border rounded-xl p-4">
                    <p className="text-sm font-medium mb-2">This Month</p>
                    <div className="text-[14px] text-gray-500 space-y-1">
                      <div className="flex items-center justify-between">
                        <span>Earnings</span>
                        <span className="font-semibold text-gray-800">
                          ₹ {financeStats.summaryMonth.toLocaleString("en-IN")}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Avg/Day</span>
                        <span className="font-semibold text-gray-800">
                          ₹{" "}
                          {Math.round(
                            financeStats.currentTotal / now.getDate(),
                          ).toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="border rounded-xl p-4">
                    <p className="text-sm font-medium mb-2">This Quarter</p>
                    <div className="text-[14px] text-gray-500 space-y-1">
                      <div className="flex items-center justify-between">
                        <span>Earnings</span>
                        <span className="font-semibold text-gray-800">
                          ₹{" "}
                          {financeStats.summaryQuarter.toLocaleString("en-IN")}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Avg/Day</span>
                        <span className="font-semibold text-gray-800">
                          ₹{" "}
                          {Math.round(
                            financeStats.currentTotal /
                              (now.getDate() + (currentMonthIndex % 3) * 30),
                          ).toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="border rounded-xl p-4">
                    <p className="text-sm font-medium mb-2">This Year</p>
                    <div className="text-[14px] text-gray-500 space-y-1">
                      <div className="flex items-center justify-between">
                        <span>Earnings</span>
                        <span className="font-semibold text-gray-800">
                          ₹ {financeStats.summaryYear.toLocaleString("en-IN")}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Avg/Day</span>
                        <span className="font-semibold text-gray-800">
                          ₹{" "}
                          {Math.round(
                            financeStats.currentTotal /
                              (now.getDate() +
                                ((currentMonthIndex - 1) % 12) * 30),
                          ).toLocaleString("en-IN")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-base font-medium">Wage Analysis</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="border rounded-xl px-3 py-2">
                    <p className="text-[14px] text-gray-500">Current rate</p>
                    <p className="text-2xl font-semibold">
                      {dailyRate != null ? `₹ ${dailyRate}` : "—"}
                    </p>
                  </div>
                  <div className="border rounded-xl px-3 py-2">
                    <p className="text-[14px] text-gray-500">
                      Effective Hourly
                    </p>
                    <p className="text-2xl font-semibold">
                      {dailyRate != null
                        ? `₹ ${(dailyRate / 8).toFixed(0)}`
                        : "—"}
                    </p>
                  </div>
                  <div className="border rounded-xl px-3 py-2">
                    <p className="text-[14px] text-gray-500">
                      Monthly Potential
                    </p>
                    <p className="text-2xl font-semibold">
                      {dailyRate != null
                        ? `₹ ${(
                            dailyRate * financeStats.totalWorkingDays
                          ).toLocaleString("en-IN")}`
                        : "—"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Wage History Section */}
              <div className="border rounded-2xl px-4 py-3">
                <p className="text-base font-medium mb-3">Wage History</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left text-gray-500">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                      <tr>
                        <th scope="col" className="px-4 py-3">
                          Amount
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Type
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Start Date
                        </th>
                        <th scope="col" className="px-4 py-3">
                          End Date
                        </th>
                        <th scope="col" className="px-4 py-3">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {workerWageHistory && workerWageHistory.length > 0 ? (
                        workerWageHistory
                          .sort(
                            (a, b) =>
                              new Date(b.start_date) - new Date(a.start_date),
                          ) // sort desc
                          .map((wage) => (
                            <tr
                              key={wage.id}
                              className="bg-white border-b hover:bg-gray-50"
                            >
                              <td className="px-4 py-3 font-medium text-gray-900">
                                ₹{" "}
                                {parseFloat(wage.amount).toLocaleString(
                                  "en-IN",
                                )}
                              </td>
                              <td className="px-4 py-3">{wage.payment_type}</td>
                              <td className="px-4 py-3">{wage.start_date}</td>
                              <td className="px-4 py-3">
                                {wage.end_date || "—"}
                              </td>
                              <td className="px-4 py-3">
                                {wage.is_active ? (
                                  <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded">
                                    Active
                                  </span>
                                ) : (
                                  <span className="bg-gray-100 text-gray-800 text-xs font-medium px-2.5 py-0.5 rounded">
                                    Inactive
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="px-4 py-3 text-center">
                            No wage history found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === "payments" && (
            <div className="p-8 space-y-6">
              {/* Payment Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border rounded-xl px-4 py-3">
                  <p className="text-[14px] text-gray-500">Total Paid</p>
                  <p className="text-2xl font-semibold text-green-600">
                    ₹ {paymentStats.totalPaid.toLocaleString("en-IN")}
                  </p>
                </div>
                <div className="border rounded-xl px-4 py-3">
                  <p className="text-[14px] text-gray-500">
                    Current Month Total
                  </p>
                  <p className="text-2xl font-semibold">
                    ₹ {financeStats.currentTotal.toLocaleString("en-IN")}
                  </p>
                </div>
                <div className="border rounded-xl px-4 py-3">
                  <p className="text-[14px] text-gray-500">Due Amount</p>
                  <p className="text-2xl font-semibold text-red-600">
                    ₹ {paymentStats.due.toLocaleString("en-IN")}
                  </p>
                </div>
              </div>

              {/* Payment Trend Chart */}
              <div className="border rounded-2xl px-4 py-3">
                <p className="text-base font-medium mb-3">
                  Payment History (This Year)
                </p>
                <div className="border rounded-xl p-3">
                  <FinanceTrend values={paymentStats.monthlyPayments} />
                  <div className="mt-2 grid grid-cols-12 text-[14px] px-4 text-gray-500 gap-x-2">
                    <span>Jan</span>
                    <span>Feb</span>
                    <span>Mar</span>
                    <span>Apr</span>
                    <span>May</span>
                    <span>Jun</span>
                    <span>Jul</span>
                    <span>Aug</span>
                    <span>Sep</span>
                    <span>Oct</span>
                    <span>Nov</span>
                    <span>Dec</span>
                  </div>
                </div>
              </div>

              {/* Payment List */}
              <div className="border rounded-2xl p-4">
                <p className="text-base font-medium mb-3">
                  Payment Transactions
                </p>
                <div className="space-y-2">
                  {payments.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      No payments recorded
                    </p>
                  ) : (
                    payments
                      .sort(
                        (a, b) =>
                          new Date(b.transaction_date) -
                          new Date(a.transaction_date),
                      )
                      .map((payment) => (
                        <div
                          key={payment.id}
                          className="border rounded-lg p-3 flex items-center justify-between hover:bg-gray-50"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <p className="font-medium">
                                ₹{" "}
                                {parseFloat(payment.amount).toLocaleString(
                                  "en-IN",
                                )}
                              </p>
                              <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                                {payment.payment_type}
                              </span>
                              <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                                {payment.transaction_method}
                              </span>
                            </div>
                            {payment.comments && (
                              <p className="text-sm text-gray-500 mt-1">
                                {payment.comments}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium">
                              {new Date(
                                payment.transaction_date,
                              ).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </p>
                            <p className="text-xs text-gray-500">
                              {new Date(payment.created_on).toLocaleTimeString(
                                "en-IN",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </p>
                            <button
                              type="button"
                              onClick={() =>
                                handleDownloadPaymentReceipt(payment)
                              }
                              disabled={downloadingPaymentId === payment.id}
                              className="mt-2 inline-flex items-center gap-1 rounded-md border border-purple-200 bg-purple-50 px-2 py-1 text-xs font-medium text-purple-700 hover:bg-purple-100 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              <Download size={12} />
                              {downloadingPaymentId === payment.id
                                ? "Generating..."
                                : "Download Receipt"}
                            </button>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showUpdateRateModal && (
        <WorkerPaymentRateModal
          initialRate={dailyRate}
          isOpen={showUpdateRateModal}
          onClose={() => setShowUpdateRateModal(false)}
          onSave={async (newRate) => {
            if (dailyRate === null) {
              await addPaymentRate(newRate);
            } else {
              await updatePaymentRate(newRate);
            }
          }}
        />
      )}
      {showEditWorkerModal && (
        <WorkerEditModal
          isOpen={showEditWorkerModal}
          onClose={() => setShowEditWorkerModal(false)}
          onSave={updateWorker}
          isSaving={isUpdatingWorker}
          initialValues={editWorkerInitialValues}
          addAlert={addAlert}
        />
      )}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={deleteWorker}
        title="Delete Worker"
        message={`Are you sure you want to delete ${workerName}? This action cannot be undone.`}
        confirmText="Delete"
        isDeleting={isDeletingWorker}
      />
    </div>
  );
};

export default WorkerProfile;
